import { Component } from 'react';
import classNames from 'classnames';
import type { WithRouterProps } from 'next/dist/client/with-router';
import { WithTranslation } from 'react-i18next';
import { GoogleOAuthProvider } from '@react-oauth/google';

import { logEvent } from 'utils/analytics';
import { ReduxProps } from './ProForm.container';
import SocialRegistration from '../../SocialRegistration/SocialRegistration';
import { createTelemetryBeacon, sendTelemetryBeacon } from 'utils/telemetry';

export interface OwnProps {
  isDesktopHidden?: boolean;
  isMobileDevice?: boolean;
}

export type Props = OwnProps & ReduxProps & WithTranslation & WithRouterProps;

interface State {
  isLoading: boolean;
}

function checkIfAuthDataInLocalStorage() {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return Boolean(localStorage.getItem('ngStorage-authData'));
  } catch (error) {
    return false;
  }
}

class ProForm extends Component<Props, State> {
  static defaultProps = {
    currentUser: undefined,
    isDesktopHidden: false,
    isMobileDevice: true,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: false,
    };
  }

  onContinue = async () => {
    const { router, currentUser } = this.props;
    const language = (router.query.language as string) || 'ja';
    const langPath = language === 'en' ? '/en' : '';
    if (currentUser?.isPro() && currentUser.isProfileComplete())
      window.location.href = `${langPath}/account/view-profile`;
    else await router.push('/sign-up/pro');
  };

  onSubmit = async () => {
    const { isDesktopHidden, router } = this.props;
    const language = (router.query.language as string) || 'ja';
    if (!isDesktopHidden) {
      logEvent(`proSignUp`, `openProForm`, `pro-new-main-banner`);
    }
    logEvent('proSignUp', 'startedRegistration', 'newProPage');
    sendTelemetryBeacon(
      createTelemetryBeacon({
        action: 'startedRegistration_nonSocial',
      })
    );
    const url = `${language === 'en' ? '/en' : ''}/sign-up/pro`;
    await router.push(url);
  };

  render() {
    const { currentUser, isMobileDevice, t, isLoadingAuth } = this.props;

    const formTitleClass = classNames({
      'form-title': !isMobileDevice,
      'form-title-mobile': isMobileDevice,
    });

    const shouldShowAuthenticatedProForm =
      currentUser?.isPro() || (checkIfAuthDataInLocalStorage() && isLoadingAuth);
    const shouldShowSocialReg = !shouldShowAuthenticatedProForm && !currentUser;

    return (
      <>
        {shouldShowAuthenticatedProForm && (
          <div className="form-container logged-in">
            <div className="text-container">{t('staticPages:pro.loggedInUser')}</div>
            <div className="btn-container">
              <button
                className="btn get-started-btn"
                onClick={this.onContinue}
                type="button"
                data-test-id="pro-page-cta"
              >
                {t('staticPages:pro.form.start')}
              </button>
            </div>
          </div>
        )}
        {!shouldShowAuthenticatedProForm && (
          <div className="form-container logged-in">
            <div className={formTitleClass}>{t('staticPages:pro.form.titleNew')}</div>
            <div className="btn-container">
              <button
                className="btn get-started-btn"
                onClick={this.onSubmit}
                type="button"
                data-test-id="pro-page-cta"
              >
                {t('staticPages:pro.form.start')}
              </button>
            </div>
            {shouldShowSocialReg && (
              <GoogleOAuthProvider clientId={process.env.GOOGLE_SIGN_IN_CLIENT_ID ?? ''}>
                <SocialRegistration />
              </GoogleOAuthProvider>
            )}
          </div>
        )}

        <style jsx>
          {`
            h3 {
              font-size: 1rem;
              font-weight: bold;
            }

            .form-container {
              display: block;
              width: 100%;
              padding: 1.5rem 4rem;
              background-color: #ffffff;
              border-radius: 10px;
              box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
            }

            .form-title-mobile {
              display: inline-block;
              padding-bottom: 7px;
            }

            .form-title {
              text-align: center;
              width: 100%;
              font-style: normal;
              font-family: 'Roboto';
              font-weight: 700;
              font-size: 16px;
              line-height: 16px;
              flex: none;
              order: 0;
              flex-grow: 0;
              padding-bottom: 9px;
            }

            .desktop-modal {
              width: 100%;
            }

            .logged-in {
              align-items: center;
              display: flex;
              justify-content: space-evenly;
              flex-direction: column;
            }

            .text-container {
              margin-bottom: 1rem;
              text-align: center;
            }

            .btn-container {
              width: 100%;
            }

            .btn {
              width: 100%;
            }

            .input-group-container {
              flex: 1;
              margin-right: 20px;
            }

            .get-started-btn {
              color: white;
              background-color: #ff698e;
              cursor: pointer;
              font-family: 'Hiragino Kaku Gothic Pro';
              font-size: 1.1rem;
              line-height: 2.07rem;
              font-weight: 600;
              padding-top: 0.9375rem;
              padding-bottom: 0.9375rem;
              text-align: center;
              box-shadow: none;
              border: none;
              border-radius: 8px;
              padding-left: 1rem;
              padding-right: 1rem;
              text-decoration: none;
              margin-right: 0;
            }
            .get-started-btn:hover {
              background-color: #f00073;
            }
            .get-started-btn:lang(ja) {
              font-family: YuGothic, 'Yu Gothic', 'Hiragino Kaku Gothic Pro', sans-serif;
            }
            p {
              margin: 0;
            }
            @media (max-width: 768px) {
              .form-container {
                padding: 15px;
              }
            }
          `}
        </style>
      </>
    );
  }
}

export default ProForm;
